/**
 * Admin Layout Component for XLR8 Admin Console
 *
 * Provides the main layout structure with navigation sidebar,
 * header, and content area for the admin console.
 */
import React, { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useMobile } from "../../hooks/useMobile";
import {
  HomeIcon,
  UsersIcon,
  BellIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ChatBubbleLeftRightIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { useRealTimeStats } from "../../hooks/useAdminData";
import { usePageTitle } from "../../hooks/usePageTitle";

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

export default function AdminLayout() {
  const location = useLocation();
  const { stats, pendingActivities } = useRealTimeStats();
  const isMobile = useMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Set browser tab title for admin pages (e.g., "XLR8 | Admin - Dashboard")
  usePageTitle(`Admin - ${getPageTitle(location.pathname)}`);

  const navigation: NavigationItem[] = [
    {
      name: "Dashboard",
      href: "/daddyslink",
      icon: HomeIcon,
    },
    {
      name: "Beta Users",
      href: "/daddyslink/users",
      icon: UsersIcon,
    },
    {
      name: "Applications",
      href: "/daddyslink/applications",
      icon: DocumentTextIcon,
    },
    {
      name: "Feedback",
      href: "/daddyslink/feedback",
      icon: ChatBubbleLeftRightIcon,
    },
    {
      name: "Ownership Records",
      href: "/daddyslink/ownership-records",
      icon: ShieldCheckIcon,
    },
    {
      name: "Activities",
      href: "/daddyslink/activities",
      icon: BellIcon,
      badge: pendingActivities > 0 ? pendingActivities : undefined,
    },
    {
      name: "Settings",
      href: "/daddyslink/settings",
      icon: Cog6ToothIcon,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile Hamburger Button */}
      {isMobile && !isSidebarOpen && (
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="fixed top-4 left-4 z-40 bg-black text-white p-3 rounded-full shadow-lg hover:bg-gray-800 transition-colors"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-50"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed inset-y-0 left-0 w-64 bg-black
          transition-transform duration-300 ease-in-out
          ${isMobile ? (isSidebarOpen ? "translate-x-0 z-[60]" : "-translate-x-full z-50") : "z-50"}
        `}
      >
        {/* Sidebar Header */}
        <div className="flex h-16 items-center justify-between border-b border-gray-800 px-4">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-white" />
            <span className="ml-2 text-xl font-bold text-white">
              XLR8 Admin
            </span>
          </div>
          {isMobile && (
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-1 hover:bg-gray-800 rounded transition-colors"
              aria-label="Close menu"
            >
              <X size={20} className="text-white" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="mt-8 px-4">
          <ul className="space-y-2">
            {navigation.map((item) => {
              const isActive =
                location.pathname === item.href ||
                (item.href !== "/daddyslink" &&
                  location.pathname.startsWith(item.href));

              return (
                <li key={item.name}>
                  <NavLink
                    to={item.href}
                    onClick={() => isMobile && setIsSidebarOpen(false)}
                    className={({ isActive: linkActive }) => {
                      const active = isActive || linkActive;
                      return `group flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        active
                          ? "bg-white text-black"
                          : "text-gray-300 hover:bg-gray-800 hover:text-white"
                      }`;
                    }}
                  >
                    <item.icon
                      className={`mr-3 h-5 w-5 flex-shrink-0 ${
                        isActive
                          ? "text-black"
                          : "text-gray-400 group-hover:text-white"
                      }`}
                    />
                    <span className="flex-1">{item.name}</span>
                    {item.badge && (
                      <span className="ml-2 inline-flex items-center justify-center rounded-full bg-red-500 px-2 py-1 text-xs font-medium text-white">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Admin Info */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-800 p-4">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center">
              <span className="text-sm font-bold text-black">S</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-white">Slink</p>
              <p className="text-xs text-gray-400">Beta Admin</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={isMobile ? "ml-0" : "ml-64"}>
        {/* Top Header */}
        <header
          className={`bg-white border-b border-gray-200 py-4 ${isMobile ? "pl-16 pr-4" : "px-6"}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-black">
                {getPageTitle(location.pathname)}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {getPageDescription(location.pathname)}
              </p>
            </div>

            {/* Quick Stats - Condensed on mobile */}
            {stats && (
              <>
                {/* Mobile: Single badge */}
                {isMobile ? (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-black text-white">
                      {stats.total_beta_users} users
                    </span>
                    {stats.pending_activities > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        {stats.pending_activities} pending
                      </span>
                    )}
                  </div>
                ) : (
                  /* Desktop: Full stats row */
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-black">
                        {stats.total_beta_users}
                      </div>
                      <div className="text-xs text-gray-500">Total Users</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-black">
                        {stats.invited_users}
                      </div>
                      <div className="text-xs text-gray-500">Invited</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-black">
                        {stats.completed_onboarding}
                      </div>
                      <div className="text-xs text-gray-500">Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {stats.pending_activities}
                      </div>
                      <div className="text-xs text-gray-500">Need Outreach</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="px-6 py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Helper functions for page titles and descriptions
function getPageTitle(pathname: string): string {
  if (pathname === "/daddyslink") return "Dashboard";
  if (pathname.startsWith("/daddyslink/users")) return "Beta Users";
  if (pathname.startsWith("/daddyslink/applications"))
    return "Beta Applications";
  if (pathname.startsWith("/daddyslink/feedback")) return "User Feedback";
  if (pathname.startsWith("/daddyslink/ownership-records"))
    return "Ownership Records";
  if (pathname.startsWith("/daddyslink/activities")) return "User Activities";
  if (pathname.startsWith("/daddyslink/settings")) return "Settings";
  return "Admin Console";
}

function getPageDescription(pathname: string): string {
  if (pathname === "/daddyslink")
    return "Overview of beta program performance and recent activity";
  if (pathname.startsWith("/daddyslink/users"))
    return "Manage beta users, generate invite links, and track onboarding";
  if (pathname.startsWith("/daddyslink/applications"))
    return "Review beta invite applications and approve new users";
  if (pathname.startsWith("/daddyslink/feedback"))
    return "Review user feedback submissions and track implementation progress";
  if (pathname.startsWith("/daddyslink/ownership-records"))
    return "View immutable ownership documentation for all audio uploads";
  if (pathname.startsWith("/daddyslink/activities"))
    return "Monitor user activities and manage outreach opportunities";
  if (pathname.startsWith("/daddyslink/settings"))
    return "Configure admin console preferences and settings";
  return "XLR8 Beta User Management";
}
